import { useId } from "react";
import type { ResultsPersonaId } from "@narrator/personaIds";
import { PERSONA_REGISTRY } from "@narrator/registry";
import { setPersonaPreference } from "../lib/personaPreference";

type PersonaPickerProps = {
	value: ResultsPersonaId;
	onChange: (personaId: ResultsPersonaId) => void;
	/** Compact for voting chrome; default is curator/create fieldset. */
	compact?: boolean;
	/**
	 * Flatten chrome when nested inside a Card (avoid double border/pad).
	 * Home create form uses this.
	 */
	embedded?: boolean;
	/** Override legend (default: Character language). */
	legend?: string;
	/** Optional help under the legend. */
	hint?: string;
};

/**
 * Accessible persona dialect picker — same six voices as Slack create/channel config.
 * Preference persists via localStorage; does not claim Slack avatar/channel APIs on web.
 */
export function PersonaPicker({
	value,
	onChange,
	compact = false,
	embedded = false,
	legend = "Character language",
	hint,
}: PersonaPickerProps) {
	const groupId = useId();
	const hintId = useId();
	const defaultHint =
		hint ??
		"Changes ballot and waiting copy on this device. Recap voice follows the style chosen when the poll was created (Slack or web).";

	const shellClass = embedded
		? "border-0 bg-transparent px-0 py-0"
		: compact
			? "rounded-xl border border-wi-border bg-wi-surface/70 px-3 py-2.5"
			: "rounded-xl border border-wi-border bg-wi-surface/80 px-4 py-3";

	return (
		<fieldset className={shellClass}>
			<legend
				className={`px-1 font-medium text-wi-text-muted text-xs uppercase tracking-wide ${embedded ? "mb-0" : ""}`}
			>
				{legend}
			</legend>
			<p id={hintId} className="mt-1 text-wi-text-muted text-xs leading-snug">
				{defaultHint}
			</p>
			<div
				className={`mt-2.5 flex flex-wrap gap-2 ${compact ? "gap-1.5" : ""}`}
				role="radiogroup"
				aria-labelledby={groupId}
				aria-describedby={hintId}
			>
				<span id={groupId} className="sr-only">
					Character language for ballots and waiting copy
				</span>
				{PERSONA_REGISTRY.map((p) => {
					const selected = value === p.id;
					return (
						<button
							key={p.id}
							type="button"
							role="radio"
							aria-checked={selected}
							title={p.exampleLine}
							className={[
								"min-h-11 rounded-lg border px-3 py-2 text-left font-medium text-sm transition-colors duration-150 ease-in-out",
								"motion-reduce:transition-none",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wi-gold/45",
								selected
									? "border-wi-gold/50 bg-wi-gold/10 text-wi-text"
									: "border-wi-border text-wi-text-secondary hover:border-wi-gold/30",
							].join(" ")}
							onClick={() => {
								setPersonaPreference(p.id);
								onChange(p.id);
							}}
						>
							<span className="block leading-snug">{p.label}</span>
							{compact ? null : (
								<span className="mt-0.5 block font-normal text-[11px] text-wi-text-muted leading-snug">
									{p.description}
								</span>
							)}
						</button>
					);
				})}
			</div>
		</fieldset>
	);
}
